/**
 * Magic Link 検証ページ (ADR-0013、PR-MAGIC-LINK-ROUTE).
 *
 * /magic-link?token=xxx で遷移してきて token を verify → ログイン後 /dashboard へ。
 *
 * petacms-admin は superadmin 専用 panel のため、verify 成功後の遷移先は
 * dashboard。authMe で saRole が無ければ /login に弾かれるので safety net 済み。
 */

import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authMagicLinkVerify, setAuthFlag } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';

export function MagicLinkPage({ token }: { token?: string }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'ログイン中… - PetaCMS Admin';
    if (!token) {
      setStatus('error');
      setError('ログインリンクが不正です。もう一度お試しください。');
      return;
    }

    (async () => {
      try {
        await authMagicLinkVerify(token);
        setAuthFlag();
        navigate({ to: '/dashboard' });
      } catch (err) {
        setStatus('error');
        setError(
          formatErrorMessage(
            err,
            'ログインリンクの有効期限が切れているか、すでに使用されています。',
          ),
        );
      }
    })();
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center">
        <h1 className="mb-4 text-[22px] font-bold text-[#23221e]">
          {status === 'verifying' ? 'ログイン中…' : 'ログインに失敗しました'}
        </h1>
        {status === 'verifying' ? (
          <p className="text-[14px] text-[#706d65]">少々お待ちください。</p>
        ) : (
          <>
            <p className="mb-6 text-[14px] text-[#991b1b]">{error}</p>
            <Link
              to="/login"
              className="inline-block h-[44px] rounded-[6px] bg-[#3b82f6] px-6 text-[15px] font-semibold leading-[44px] text-white transition hover:bg-[#1d4ed8]"
            >
              ログイン画面へ戻る
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
